import {
  Button,
  Dialog,
  Select,
  Spinner,
  Text,
} from '@risksmart-app/atomic-ui';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import { Risk_Scoring_Model_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { type FC, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ControlledJsonEditor } from 'src/components/form/controlled-json-editor/ControlledJsonEditor';
import ControlledSelect from 'src/components/form/controlled-select';
import { useRecalculateRiskScores } from 'src/hooks/mutations/aggregation/useRecalculateRiskScores';
import { useIsCustomerSupport } from 'src/hooks/useIsCustomerSupport';

type RoundingMode = 'nearest' | 'up' | 'down';

const ROUNDING_MODES: RoundingMode[] = ['nearest', 'up', 'down'];
const DEFAULT_ROUNDING_MODE: RoundingMode = 'nearest';

const isRoundingMode = (value: unknown): value is RoundingMode =>
  typeof value === 'string' &&
  (ROUNDING_MODES as readonly string[]).includes(value);

/**
 * Parse the RiskScoringModelConfig JSON string. Treat empty strings and
 * invalid JSON as an empty object so the dropdown can still render and write
 * without clobbering existing config — see RSP-5817.
 */
const parseConfigSafely = (raw: unknown): Record<string, unknown> => {
  if (typeof raw !== 'string' || raw.trim() === '') {
    return {};
  }
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }

    return {};
  } catch {
    return {};
  }
};

const readRoundingMode = (raw: unknown): RoundingMode => {
  const config = parseConfigSafely(raw);
  const mode = config.roundingMode;

  return isRoundingMode(mode) ? mode : DEFAULT_ROUNDING_MODE;
};

export const RiskScoreSettings: FC = () => {
  const { control, setValue } = useFormContext();
  const { t } = useTranslation('common', { keyPrefix: 'modules.fields' });
  const { t: ct } = useTranslation('common');
  const isCustomerSupport = useIsCustomerSupport();
  const { addNotification } = useNotifications();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const riskScoringModel = useWatch({ control, name: 'RiskScoringModel' });
  const riskScoringModelConfig = useWatch({
    control,
    name: 'RiskScoringModelConfig',
  });

  const showRoundingMode =
    riskScoringModel ===
      Risk_Scoring_Model_Enum.TypedControlEffectivenessAverages ||
    riskScoringModel === Risk_Scoring_Model_Enum.RiskAggregationOnly;

  const currentRoundingMode = readRoundingMode(riskScoringModelConfig);

  const roundingModeOptions: { label: string; value: RoundingMode }[] = useMemo(
    () =>
      ROUNDING_MODES.map((mode) => ({
        value: mode,
        label: t(`RiskScoringRoundingModes.${mode}` as const),
      })),
    [t]
  );

  const handleRoundingModeChange = (nextMode: RoundingMode) => {
    const config = parseConfigSafely(riskScoringModelConfig);
    const next = { ...config, roundingMode: nextMode };
    setValue('RiskScoringModelConfig', JSON.stringify(next, null, 2), {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const { recalculateRiskScores, loading: recalculating } =
    useRecalculateRiskScores();

  const handleRecalculate = async () => {
    setShowConfirmModal(false);
    try {
      await recalculateRiskScores();
      addNotification({
        type: 'success',
        content: t('RecalculateRiskScores_success'),
      });
    } catch {
      addNotification({
        type: 'error',
        content: t('RecalculateRiskScores_error'),
      });
    }
  };

  return (
    <div className={'flex flex-col gap-4'}>
      <ControlledSelect
        control={control}
        testId={'riskScoringModel'}
        name={'RiskScoringModel'}
        label={t('RiskScoringModel')}
        options={[
          {
            label: t('RiskScoringModels.default'),
            value: Risk_Scoring_Model_Enum.Default,
          },
          {
            label: t('RiskScoringModels.control_effectiveness_averages'),
            value: Risk_Scoring_Model_Enum.ControlEffectivenessAverages,
          },
          {
            label: t('RiskScoringModels.typed_control_effectiveness_averages'),
            value: Risk_Scoring_Model_Enum.TypedControlEffectivenessAverages,
          },
          {
            label: t('RiskScoringModels.risk_aggregation_only'),
            value: Risk_Scoring_Model_Enum.RiskAggregationOnly,
          },
        ]}
      />
      {showRoundingMode && (
        <div
          data-testid={'form-field-risk-scoring-rounding-mode'}
          className={'flex flex-col gap-1'}
        >
          <Text preset={'body-md'}>{t('RiskScoringRoundingMode')}</Text>
          <Text preset={'body-sm'} className={'text-muted-foreground'}>
            {t('RiskScoringRoundingMode_help')}
          </Text>
          <div data-testid={'risk-scoring-rounding-mode'}>
            <Select
              items={roundingModeOptions}
              value={currentRoundingMode}
              onValueChange={(value) => {
                if (isRoundingMode(value)) {
                  handleRoundingModeChange(value);
                }
              }}
            />
          </div>
        </div>
      )}
      <ControlledJsonEditor
        control={control}
        name={'RiskScoringModelConfig'}
        label={t('RiskScoringModelConfig')}
      />
      {isCustomerSupport && (
        <>
          <Button
            data-testid={'recalculate-risk-scores-button'}
            variant={'neutral'}
            style={'outline'}
            className={'w-fit'}
            onClick={() => setShowConfirmModal(true)}
            disabled={recalculating}
          >
            {recalculating && <Spinner />}
            {t('RecalculateRiskScores')}
          </Button>
          <Dialog
            open={showConfirmModal}
            onOpenChange={(open) => {
              if (!open) {
                setShowConfirmModal(false);
              }
            }}
            size={'sm'}
          >
            <Dialog.Header title={t('RecalculateRiskScores_confirm_header')} />
            <Dialog.Body>
              <div data-testid={'recalculate-risk-scores-modal'}>
                {t('RecalculateRiskScores_confirm_body')}
              </div>
            </Dialog.Body>
            <Dialog.Footer>
              <Button
                variant={'neutral'}
                style={'outline'}
                onClick={() => setShowConfirmModal(false)}
              >
                {ct('cancel')}
              </Button>
              <Button onClick={handleRecalculate}>
                {t('RecalculateRiskScores_confirm_button')}
              </Button>
            </Dialog.Footer>
          </Dialog>
        </>
      )}
    </div>
  );
};
